'use client';

import { useEffect } from 'react';
import { refreshAccessToken, RefreshUnavailableError } from './axios-auth';

// Access tokens expire every ACCESS_TOKEN_EXPIRE_MINUTES (60, see
// services/auth-service/.env). Until now, refreshing only ever happened
// REACTIVELY -- after some request had already failed with a 401
// (attachAuthResponseInterceptor / fetchWithAuthRetry). That's correct but
// leaves a one-shot failure window: if the single refresh attempt that
// follows that 401 hits any transient hiccup (RefreshUnavailableError), the
// triggering request is left failed and surfaces its raw error (e.g. a PUT
// .../extraction failing with "Authentication required") even though the
// session itself is still perfectly valid -- the user just has to notice
// and retry the same action again.
//
// This runs a silent background refresh well before the token is due to
// expire, so in normal use the reactive 401 path rarely ever has to fire at
// all -- there's no stale token left for a mid-task action (e.g. a long
// Work Order OCR review + edit + Save session) to trip over. Purely
// opportunistic: any failure here (including a genuinely rejected refresh
// token) is swallowed and left for the next real request's reactive 401
// handling to decide on -- this background timer never redirects to login
// or clears storage itself.
const PROACTIVE_REFRESH_INTERVAL_MS = 20 * 60 * 1000; // 20 min — comfortably under the 60 min token lifetime

export function useProactiveTokenRefresh(): void {
  useEffect(() => {
    const tick = () => {
      if (!localStorage.getItem('refresh_token')) return;
      refreshAccessToken().catch((err) => {
        if (err instanceof RefreshUnavailableError) return; // transient — next tick (or a real request) tries again
        // A genuine rejection: leave storage/redirect decisions to the
        // reactive 401 path on the next real request, not this timer.
      });
    };

    const id = setInterval(tick, PROACTIVE_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);
}
